import { Fragment } from 'react';

import { NsPanel } from 'components/common/ns-panel/NsPanel';
import { NsAccordion } from 'components/common/ns-accordion/NsAccordion';
import { NsExpander } from 'components/common/ns-expander/NsExpander';
import { NsList } from 'components/common/ns-list/NsList';
import { NsAnchor } from 'components/common/ns-anchor/NsAnchor';
import { HELP_CONTACT_NUMBER } from 'config/app-config';

export const testId = 'heatPumpFAQTestId';

export const HeatPumpFAQ = () => {
  return (
    <NsPanel testId={testId}>
      <NsAccordion heading="About our heat pump add-on" headingTag="h3">
        <NsExpander
          heading="How do you estimate my heat pump consumption?"
          HeadingTag="h4"
        >
          <p>
            We estimate your heat pump consumption using the figure provided in
            your heat pump performance document given to you as part of your
            technical survey and in the email we sent you.
          </p>
        </NsExpander>
        <NsExpander
          heading="Do I need to install additional hardware?"
          HeadingTag="h4"
        >
          <p>
            No, our heat pump offer has no impact on your heat pump
            installation.
          </p>
        </NsExpander>
        <NsExpander
          heading="What if I want to change my heat pump settings?"
          HeadingTag="h4"
        >
          <p>
            Our heat pump offer won&apos;t affect your heat pump schedule or
            settings. You can change your schedule through your heat pump
            manufacturer&apos;s app.
          </p>
        </NsExpander>
      </NsAccordion>

      <NsAccordion heading="Eligibility" headingTag="h3">
        <NsExpander
          heading="My circumstances have changed, am I still eligible for your heat pump add-on?"
          HeadingTag="h4"
        >
          <p>You won&apos;t be eligible for our heat pump add-on if you:</p>
          <NsList>
            {[
              `Change supplier or move home`,
              `Have prepayment meters installed`,
              `Switch to a tariff on our old system`
            ]}
          </NsList>
          <p>
            If this happens, we&apos;ll write to you to let you know that
            we&apos;ve removed the offer from your account and add any credits
            that are due. Please note, it can sometimes take longer than normal
            to add these.
          </p>
        </NsExpander>
      </NsAccordion>

      <NsAccordion heading="Payments" headingTag="h3">
        <NsExpander heading="How do you calculate my payments?" HeadingTag="h4">
          <p>
            Your credit is equal to your estimated heat pump consumption in the
            previous month multiplied by the difference between your tariff unit
            rate and your cheaper heat pump add-on unit rate.
          </p>
          <p>
            If you&apos;re on a time-of-use tariff (e.g. Electric Driver or an
            Economy 7 tariff), we&apos;ll assume that all electricity used by
            your heat pump was used at peak times.
          </p>
        </NsExpander>
        <NsExpander heading="Will my Direct Debit change?" HeadingTag="h4">
          <p>
            Your Direct Debit won&apos;t be affected by your heat pump credits.
            You can request a refund for account credits by{' '}
            <NsAnchor href="https://www.britishgas.co.uk/energy/contact-us">
              chatting online
            </NsAnchor>{' '}
            to one of our advisors. If you have any credits left if you leave
            British Gas, these will be added to your final British Gas bill.
          </p>
        </NsExpander>
        <NsExpander
          heading="If I change tariff, will my payments change?"
          HeadingTag="h4"
        >
          <p>
            Yes, if you change tariff, we&apos;ll work out your credits with
            your new unit rate from the day your new tariff starts.
          </p>
        </NsExpander>
        <NsExpander heading="What if I leave British Gas?" HeadingTag="h4">
          <p>
            If you leave British Gas any payments due to you will be calculated
            on a pro-rata basis.
          </p>
        </NsExpander>
      </NsAccordion>

      <NsAccordion heading="Help & Support" headingTag="h3">
        <NsExpander
          heading="What consumption figures do I use when getting a quote?"
          HeadingTag="h4"
        >
          <p>
            After your heat pump&apos;s installed your annual electricity and
            gas consumption will change, for example, you&apos;ll use more
            electricity than you previously did and may no longer use gas at
            your property. Or you may use a lot less gas than you used to.
            It&apos;s important when you sign up or change your energy tariff
            that you use the correct consumption figures for your property so
            your bills are accurate.
          </p>
          <NsList>
            {[
              <Fragment key={0}>
                <b>Electricity Quote:</b> Electricity Quote: You can find the
                figure for your estimated total household electricity
                consumption in the email we sent you asking you if you&apos;d
                like the heat pump offer. You can also find it in the leaflet
                left as part of your technical survey or by calling us on{' '}
                <NsAnchor href={`tel:${HELP_CONTACT_NUMBER.HEAT_PUMP}`}>
                  {HELP_CONTACT_NUMBER.HEAT_PUMP_LABEL}.
                </NsAnchor>
              </Fragment>,
              <Fragment key={1}>
                <b>Gas Quote:</b> If you still use gas at your property after
                your heat pump&apos;s been installed, for example, if you have a
                gas cooker, you may need to estimate your gas consumption.
                Typically, gas cooking makes up about 2.5% of household annual
                gas consumption (according{' '}
                <NsAnchor href="https://www.which.co.uk/reviews/ground-and-air-source-heat-pumps/article/ground-and-air-source-heat-pumps/air-source-heat-pump-costs-and-savings-akySY6N5Y6Dd">
                  Which?
                </NsAnchor>
                ). So a typical household using 11,500 kWh of gas per year,
                could use 288 kWh for cooking after the heat pump was installed.
              </Fragment>
            ]}
          </NsList>
        </NsExpander>
        <NsExpander
          heading="I no longer use gas at my property, can my gas supply be capped?"
          HeadingTag="h4"
        >
          <p>
            Yes, we can cap your gas meter for free. Simply{' '}
            <NsAnchor href="https://www.britishgas.co.uk/energy/contact-us">
              chat online
            </NsAnchor>{' '}
            to one of our advisers. Please note, even if you no longer use gas
            at your property, you&apos;ll continue to pay a daily standing
            charge if you have a gas meter installed which isn&apos;t capped. If
            you&apos;d like to remove your gas meter, you&apos;re liable to pay
            any standing charge accrued until the meter is capped. We can only
            arrange to have a gas meter capped with the consent of the owner of
            the property.
          </p>
        </NsExpander>
        <NsExpander
          heading="I haven’t received my account credit?"
          HeadingTag="h4"
        >
          <p>
            Your credit will appear on your next statement as &apos;Heat Pump
            Credit&apos;. We aim to apply your credit within the first week of
            each calendar month but sometimes it can take a little longer. If
            you don&apos;t see your credit on your next statement, please{' '}
            <NsAnchor href="https://www.britishgas.co.uk/energy/contact-us">
              chat online
            </NsAnchor>{' '}
            to one of our advisers who can look into this for you.
          </p>
        </NsExpander>
        <NsExpander
          heading="I no longer want the Heat Pump add-on"
          HeadingTag="h4"
        >
          <p>
            To opt out of the heat pump offer, you can{' '}
            <NsAnchor href="https://www.britishgas.co.uk/energy/contact-us">
              chat online
            </NsAnchor>{' '}
            to one of our advisers.
          </p>
        </NsExpander>
      </NsAccordion>
    </NsPanel>
  );
};
